// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import ChevronDown20 from '@carbon/icons-react/es/chevron--down/20';
import Close16 from '@carbon/icons-react/es/close/16';
import Close20 from '@carbon/icons-react/es/close/20';
import { List, Space } from 'antd';
import classNames from 'classnames';
import { FC, forwardRef } from 'react';
import ReactSelect, {
  defaultTheme,
  Props,
  components as RsComponents,
  SelectInstance,
} from 'react-select';
import ReactCreatableSelect, { CreatableProps } from 'react-select/creatable';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { HeightStyle } from 'components/atoms/input/input';
import { ClassNameOnlyProps } from 'components/ozone-component';
import { Color } from '../../colors';
import { Avatar } from '../avatar';
import { Tag as TagAtom } from '../tag';
import {
  AvatarOption,
  StyledClearIndicator,
  StyledDropdownIndicator,
  StyledMultiValueLabel,
  StyledMultiValueRemove,
  StyledSingleValue,
  StyledTagOption,
} from './custom-components';
import { UserTag } from './select';
import { SelectMode } from './types';

const DropdownIndicator = (props) => {
  return (
    <StyledDropdownIndicator {...props}>
      <ChevronDown20 />
    </StyledDropdownIndicator>
  );
};

const StyledControl = styled(RsComponents.Control).attrs({
  theme: {
    ...defaultTheme,
  },
})`
  outline: 2px solid !important;
  outline-color: transparent !important;
  outline-offset: -2px;
  border: 0;
  border-bottom: 1px solid var(--gray-50);
  border-radius: 0;
  background-color: var(--coolest-gray-10);
  min-height: ${HeightStyle};
  ${ifProp(
    'isFocused',
    css`
      box-shadow: none;
      border-bottom-color: transparent !important;
      outline-color: var(--primary-color) !important;
      outline-offset: -2px;
    `,
  )};
  &:hover {
    background-color: var(--hover-color);
  }
`;

const Control = (props) => <StyledControl {...props} />;

const ClearIndicator = (props) => (
  <StyledClearIndicator {...props}>
    <Close20 />
  </StyledClearIndicator>
);

const ListItem = styled(List.Item)`
  padding: 0;
`;

const ListItemMeta = styled(List.Item.Meta)`
  align-items: center;

  & .ant-list-item-meta-title {
    margin-bottom: 0;
    color: var(--gray-100);
  }

  & .ant-list-item-meta-description {
    font-size: var(--font-size-sm);
    color: var(--gray-80);
    font-weight: normal;
  }
`;

const Option = (props) => {
  const { data } = props;

  return (
    <StyledTagOption {...props}>
      <ListItem>
        <ListItemMeta
          avatar={
            <Avatar
              name={data.__isNew__ ? data.value : data.label}
              src={data.imageUrl}
            />
          }
          title={data.label}
          description={data.description}
        />
      </ListItem>
    </StyledTagOption>
  );
};

export const StyledMultiValue = styled(RsComponents.MultiValue).attrs({
  theme: {
    ...defaultTheme,
  },
})`
  --tag-border-color: var(--tag-surface-color);

  border-radius: var(--border-radius-default);
  margin: var(--spacing-01) 0;
  padding: 0;
  border: 2px solid var(--tag-border-color);
  background: var(--tag-surface-color);
  color: var(--tag-color);
  font-size: var(--font-size-sm);
  line-height: var(--spacing-05);
  display: inline-flex;
  align-items: center;

  &:focus {
    --tag-border-color: var(--blue-60);
  }

  &.disabled,
  &[disabled] {
    --tag-surface-color: var(--gray-10);
    --tag-color: var(--gray-30);
  }

  ${({ color }) => css`
    --tag-surface-color: var(--ozone-${color}-surface);
    --tag-color: var(--ozone-${color});
  `};

  cursor: ${ifProp('onClick', 'pointer', 'initial')};
`;

const SingleValue = ({ data, ...props }) => {
  return (
    <StyledSingleValue {...props}>
      <UserTag
        color={data.color}
        imageUrl={data.imageUrl}
        label={data.label}
        userStatus={data?.userStatus}
      />
    </StyledSingleValue>
  );
};

const MultiValue = (props) => {
  const { data } = props;

  return <StyledMultiValue color={data?.color || 'gray'} {...props} />;
};

export const MultiValueLabel = (props) => {
  const { data } = props;

  return (
    <StyledMultiValueLabel {...props}>
      <Space size="small">
        <Avatar size="small" src={data.imageUrl} name={data.label} />
        <div>{data.label}</div>
      </Space>
    </StyledMultiValueLabel>
  );
};

export const MultiValueRemove = ({ data, ...props }) => (
  <StyledMultiValueRemove {...props}>
    <Close16 />
  </StyledMultiValueRemove>
);

const TagOption = (props) => {
  const { data } = props;

  return (
    <StyledTagOption {...props}>
      <TagAtom color={data.color}>{data.label}</TagAtom>
    </StyledTagOption>
  );
};

const TagSingleValue = (props) => {
  const { data } = props;

  return (
    <StyledSingleValue {...props}>
      <TagAtom color={data.color}>{data.label}</TagAtom>
    </StyledSingleValue>
  );
};

interface Option {
  label?: string;
  value?: string | number;
  count?: Color;
  description?: string;
  imageUrl?: string;
}

interface BaseSelectProps {
  type?: SelectMode;
  options: Option[];
}

export type CreatableSelectProps = BaseSelectProps &
  Omit<CreatableProps<Option, boolean>, 'options'> &
  Omit<Props, 'options'>;

export const CreatableSelect: FC<
  React.PropsWithChildren<CreatableSelectProps & ClassNameOnlyProps>
> = ({ className, components = {}, ...props }) => {
  const { type } = props;

  let customComponents: any = {
    DropdownIndicator,
    ClearIndicator,
    Control,
    MultiValueRemove,
  };

  if (type === 'user') {
    customComponents = {
      ...customComponents,
      Option,
      SingleValue,
      MultiValue,
      MultiValueLabel,
    };
  } else if (type === 'tag') {
    customComponents = {
      ...customComponents,
      Option: TagOption,
      SingleValue: TagSingleValue,
      MultiValue,
    };
  }

  return (
    <ReactCreatableSelect
      className={classNames('ozone-select-container', className)}
      classNamePrefix="ozone-select"
      theme={(theme) => ({
        ...theme,
        borderRadius: 0,
        colors: {
          ...(theme.colors || {}),
          primary: 'var(--primary-color)',
        },
      })}
      components={{
        ...customComponents,
        ...components,
      }}
      {...props}
    />
  );
};

type SelectProps = BaseSelectProps & Omit<Props, 'options'>;

export const Select = forwardRef<SelectInstance, SelectProps>(
  ({ className, components = {}, ...props }, ref) => {
    const { type = 'basic' } = props;

    let customComponents: any = {
      DropdownIndicator,
      ClearIndicator,
      Control,
      MultiValueRemove,
    };

    if (type === 'user') {
      customComponents = {
        ...customComponents,
        Option: AvatarOption,
        SingleValue,
        MultiValue,
        MultiValueLabel,
      };
    } else if (type === 'tag') {
      customComponents = {
        ...customComponents,
        Option: TagOption,
        SingleValue: TagSingleValue,
        MultiValue,
      };
    }

    /* TODO:: Remove this duplicate and make it into single. Note: I am stupidly sleepy while writing this component. So just POC implementation. */
    return (
      <ReactSelect
        ref={ref}
        className={classNames('ozone-select-container', className)}
        classNamePrefix="ozone-select"
        theme={(theme) => ({
          ...theme,
          borderRadius: 0,
          colors: {
            ...(theme.colors || {}),
            primary: 'var(--primary-color)',
          },
        })}
        components={{
          ...customComponents,
          ...components,
        }}
        {...props}
      />
    );
  },
);

Select.displayName = 'Select';
